import { motion } from "framer-motion";

interface GenieMistProps {
  brandColor: string;
  secondaryColor?: string | null;
  /** Scales the whole effect's intensity (0–1). Default 1. */
  intensity?: number;
}

/**
 * Soft brand-tinted "genie mist" — a handful of blurred gradient wisps that
 * swirl in, peak, then clear, so whatever sits on top feels *summoned* out of
 * smoke rather than just faded in. Decorative + pointer-transparent; meant to
 * live behind content during a transition. Brand blue → magenta only, low
 * opacity, heavy blur — elegant haze, never a solid blob.
 */
export const GenieMist = ({ brandColor, secondaryColor, intensity = 1 }: GenieMistProps) => {
  const end = secondaryColor || "#FF4DB8";

  // Hand-placed wisps so the mist reads as a considered swirl, not noise.
  const wisps = [
    { top: "16%", left: "22%", size: 300, color: brandColor, dx: 26, dy: -22, peak: 0.5, delay: 0 },
    { top: "30%", left: "62%", size: 340, color: end, dx: -30, dy: -16, peak: 0.45, delay: 0.06 },
    { top: "58%", left: "34%", size: 360, color: brandColor, dx: 18, dy: 24, peak: 0.4, delay: 0.12 },
    { top: "66%", left: "70%", size: 300, color: end, dx: -22, dy: 18, peak: 0.42, delay: 0.1 },
    { top: "44%", left: "48%", size: 280, color: "#FFFFFF", dx: 0, dy: -28, peak: 0.55, delay: 0.04 },
  ];

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {wisps.map((w, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.55, x: -w.dx, y: -w.dy }}
          animate={{
            opacity: [0, w.peak * intensity, 0],
            scale: [0.55, 1.25, 1.6],
            x: [-w.dx, 0, w.dx],
            y: [-w.dy, 0, w.dy],
          }}
          transition={{
            duration: 1.1,
            delay: w.delay,
            ease: "easeOut",
            times: [0, 0.45, 1],
          }}
          className="absolute rounded-full"
          style={{
            top: w.top,
            left: w.left,
            width: w.size,
            height: w.size,
            marginLeft: -w.size / 2,
            marginTop: -w.size / 2,
            background: `radial-gradient(circle, ${w.color}66 0%, ${w.color}22 40%, transparent 70%)`,
            filter: "blur(34px)",
          }}
        />
      ))}
    </div>
  );
};

export default GenieMist;
